import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { 
  TrendingUp, TrendingDown, Minus, ArrowRight, Clock,
  AlertTriangle, Sparkles, CheckCircle2, Target,
  Send, RefreshCw, ChevronRight, Zap, Calendar,
  Shield, FileText, Users, DollarSign, Briefcase,
  Heart, BarChart3, Activity
} from 'lucide-react';
import { Card, CardContent } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { toast } from 'sonner';
import { useAuth } from '../../contexts/AuthContext';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// ============ HELPER FUNCTIONS ============

function getGreeting() {
  const hour = new Date().getHours();
  if (hour < 12) return 'Good morning';
  if (hour < 17) return 'Good afternoon';
  return 'Good evening';
}

function getTrendIcon(trend) {
  if (trend === 'improving') return <TrendingUp className="w-3.5 h-3.5 text-emerald-500" />;
  if (trend === 'declining') return <TrendingDown className="w-3.5 h-3.5 text-red-500" />;
  return <Minus className="w-3.5 h-3.5 text-slate-400" />;
}

function getHealthColor(score) {
  if (score >= 90) return 'text-emerald-600';
  if (score >= 75) return 'text-amber-600';
  if (score >= 60) return 'text-orange-600';
  return 'text-red-600';
}

function getHealthBg(score) {
  if (score >= 90) return 'bg-emerald-50 border-emerald-200';
  if (score >= 75) return 'bg-amber-50 border-amber-200';
  if (score >= 60) return 'bg-orange-50 border-orange-200';
  return 'bg-red-50 border-red-200';
}

function getPriorityColor(priority) {
  if (priority === 'p0') return 'bg-red-500';
  if (priority === 'p1') return 'bg-orange-500';
  if (priority === 'p2') return 'bg-amber-500';
  return 'bg-slate-400';
}

function getSeverityColor(severity) {
  if (severity === 'critical') return 'border-l-red-500 bg-red-50/50';
  if (severity === 'high') return 'border-l-orange-500 bg-orange-50/50';
  if (severity === 'medium') return 'border-l-amber-500 bg-amber-50/50';
  return 'border-l-slate-300 bg-slate-50/50';
}

// ============ SUB-COMPONENTS ============

// Health Index Ring
function HealthRing({ score, size = 120 }) {
  const radius = (size - 12) / 2;
  const circumference = 2 * Math.PI * radius;
  const offset = circumference - (score / 100) * circumference;
  
  const color = score >= 90 ? '#10b981' : score >= 75 ? '#f59e0b' : score >= 60 ? '#f97316' : '#ef4444';
  
  return (
    <div className="relative" style={{ width: size, height: size }}>
      <svg className="transform -rotate-90" width={size} height={size}>
        <circle
          cx={size / 2}
          cy={size / 2}
          r={radius}
          stroke="#e5e7eb"
          strokeWidth="8"
          fill="none"
        />
        <circle
          cx={size / 2}
          cy={size / 2}
          r={radius}
          stroke={color}
          strokeWidth="8"
          fill="none"
          strokeLinecap="round"
          strokeDasharray={circumference}
          strokeDashoffset={offset}
          className="transition-all duration-1000 ease-out"
        />
      </svg>
      <div className="absolute inset-0 flex flex-col items-center justify-center">
        <span className={`text-3xl font-bold ${getHealthColor(score)}`}>{score}</span>
        <span className="text-[10px] uppercase tracking-wider text-slate-500">Health</span>
      </div>
    </div>
  );
}

// Priority Action Card
function PriorityCard({ action, index, onNavigate }) {
  return (
    <div 
      className="group relative bg-white rounded-xl border border-slate-200 p-4 hover:shadow-lg hover:border-slate-300 transition-all cursor-pointer"
      onClick={() => onNavigate(action.action_url)}
      data-testid={`priority-action-${index}`}
    >
      <div className="flex items-start gap-3">
        <div className={`w-1.5 h-full absolute left-0 top-0 rounded-l-xl ${getPriorityColor(action.priority)}`} />
        <div className="flex-1 min-w-0 pl-2">
          <div className="flex items-center gap-2 mb-1">
            <span className={`text-[10px] uppercase tracking-wide font-semibold px-1.5 py-0.5 rounded ${
              action.priority === 'p0' ? 'bg-red-100 text-red-700' :
              action.priority === 'p1' ? 'bg-orange-100 text-orange-700' :
              'bg-amber-100 text-amber-700'
            }`}>
              {action.priority.toUpperCase()}
            </span>
            <span className="text-xs text-slate-500">{action.estimated_minutes} min</span>
          </div>
          <h4 className="font-medium text-slate-900 text-sm leading-tight mb-1 group-hover:text-blue-600 transition-colors">
            {action.title}
          </h4>
          <p className="text-xs text-slate-500 line-clamp-1">{action.why_it_matters}</p>
        </div>
        <ArrowRight className="w-4 h-4 text-slate-300 group-hover:text-blue-500 group-hover:translate-x-0.5 transition-all shrink-0 mt-1" />
      </div>
    </div>
  );
}

// Health Metric Card
function HealthMetric({ label, score, trend, icon: Icon }) {
  return (
    <div className={`rounded-lg border p-3 ${getHealthBg(score)}`} data-testid={`health-${label.toLowerCase().replace(/\s/g, '-')}`}>
      <div className="flex items-center justify-between mb-1">
        <Icon className="w-4 h-4 text-slate-500" />
        {getTrendIcon(trend)}
      </div>
      <div className={`text-xl font-bold ${getHealthColor(score)}`}>{score}%</div>
      <div className="text-[10px] uppercase tracking-wide text-slate-600">{label}</div>
    </div>
  );
}

// Risk Card
function RiskCard({ risk, onNavigate }) {
  return (
    <div 
      className={`border-l-4 rounded-r-lg p-3 cursor-pointer hover:shadow-md transition-shadow ${getSeverityColor(risk.severity)}`}
      onClick={() => risk.action_url && onNavigate(risk.action_url)}
      data-testid={`risk-${risk.id}`}
    >
      <div className="flex items-start justify-between gap-2">
        <div className="flex-1 min-w-0">
          <p className="text-sm font-medium text-slate-900 leading-tight">{risk.title}</p>
          <p className="text-xs text-slate-600 mt-0.5">{risk.when}</p>
        </div>
        <span className={`text-[10px] uppercase tracking-wide font-semibold px-1.5 py-0.5 rounded shrink-0 ${
          risk.severity === 'critical' ? 'bg-red-100 text-red-700' :
          risk.severity === 'high' ? 'bg-orange-100 text-orange-700' :
          'bg-amber-100 text-amber-700'
        }`}>
          {risk.severity}
        </span>
      </div>
    </div>
  );
}

// Opportunity Card
function OpportunityCard({ opportunity, onNavigate }) {
  return (
    <div 
      className="flex items-center gap-3 p-3 rounded-lg bg-emerald-50/50 border border-emerald-200 cursor-pointer hover:shadow-md hover:bg-emerald-50 transition-all"
      onClick={() => opportunity.action_url && onNavigate(opportunity.action_url)}
      data-testid={`opportunity-${opportunity.id}`}
    >
      <div className="w-8 h-8 rounded-full bg-emerald-100 flex items-center justify-center shrink-0">
        <Sparkles className="w-4 h-4 text-emerald-600" />
      </div>
      <div className="flex-1 min-w-0">
        <p className="text-sm font-medium text-slate-900 truncate">{opportunity.title}</p>
        <p className="text-xs text-emerald-700">{opportunity.benefit}</p>
      </div>
      <ChevronRight className="w-4 h-4 text-emerald-400 shrink-0" />
    </div>
  );
}

// Timeline Event
function TimelineEvent({ event, onNavigate }) {
  const isUrgent = event.is_critical || event.requires_action;
  
  return (
    <div 
      className={`flex items-start gap-3 py-2 ${event.action_url ? 'cursor-pointer hover:bg-slate-50 -mx-2 px-2 rounded-lg' : ''}`}
      onClick={() => event.action_url && onNavigate(event.action_url)}
    >
      <div className="w-14 text-right shrink-0">
        <span className={`text-xs font-medium ${isUrgent ? 'text-red-600' : 'text-slate-500'}`}>
          {event.display_time}
        </span>
      </div>
      <div className={`w-2 h-2 rounded-full mt-1.5 shrink-0 ${isUrgent ? 'bg-red-500' : 'bg-slate-300'}`} />
      <div className="flex-1 min-w-0">
        <p className={`text-sm ${isUrgent ? 'font-medium text-red-900' : 'text-slate-700'}`}>
          {event.title}
        </p>
      </div>
    </div>
  );
}

// Ask Leynix Input
function AskAligned({ onSubmit, isLoading }) {
  const [query, setQuery] = useState('');
  
  const handleSubmit = (e) => {
    e.preventDefault();
    if (query.trim()) {
      onSubmit(query);
      setQuery('');
    }
  };
  
  return (
    <form onSubmit={handleSubmit} className="relative" data-testid="ask-aligned">
      <input
        type="text"
        value={query}
        onChange={(e) => setQuery(e.target.value)}
        placeholder="Ask Leynix anything... Who has dementia experience? What delayed billing?"
        className="w-full pl-4 pr-12 py-3 rounded-xl border border-slate-200 bg-white text-sm placeholder:text-slate-400 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all"
        disabled={isLoading}
      />
      <button
        type="submit"
        disabled={!query.trim() || isLoading}
        className="absolute right-2 top-1/2 -translate-y-1/2 p-2 rounded-lg bg-blue-500 text-white hover:bg-blue-600 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
      >
        <Send className="w-4 h-4" />
      </button>
    </form>
  );
}

// ============ MAIN COMPONENT ============

export default function OperationsCoordinator() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [briefing, setBriefing] = useState(null);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [askLoading, setAskLoading] = useState(false);
  const [aiResponse, setAiResponse] = useState(null);
  
  const fetchBriefing = useCallback(async (showToast = false) => {
    try {
      if (showToast) setRefreshing(true);
      else setLoading(true);
      
      const res = await fetch(`${API_URL}/api/ops-coordinator/briefing`, {
        credentials: 'include'
      });
      
      if (!res.ok) throw new Error('Failed to load briefing');
      
      const data = await res.json();
      setBriefing(data);
      
      if (showToast) toast.success('Briefing refreshed');
    } catch (err) {
      console.error('Error loading briefing:', err);
      toast.error('Failed to load operations briefing');
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  }, []);
  
  useEffect(() => {
    fetchBriefing();
  }, [fetchBriefing]);
  
  const handleNavigate = (url) => {
    if (url) {
      // Handle both absolute and relative URLs
      if (url.startsWith('/')) {
        navigate(url);
      } else {
        window.location.href = url;
      }
    }
  };
  
  const handleAskAligned = async (query) => {
    try {
      setAskLoading(true);
      setAiResponse(null);
      
      const res = await fetch(`${API_URL}/api/ai/query`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ query })
      });
      
      if (!res.ok) throw new Error('Query failed');
      
      const data = await res.json();
      setAiResponse(data);
    } catch (err) {
      console.error('Ask Leynix error:', err);
      toast.error('Failed to process your question');
    } finally {
      setAskLoading(false);
    }
  };
  
  // Loading state
  if (loading) {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center" data-testid="briefing-loading">
        <div className="text-center">
          <div className="w-16 h-16 rounded-full bg-gradient-to-br from-blue-500 to-indigo-600 mx-auto mb-4 animate-pulse" />
          <p className="text-slate-600">Preparing your briefing...</p>
        </div>
      </div>
    );
  }
  
  if (!briefing) {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center">
        <div className="text-center">
          <p className="text-slate-600">Unable to load briefing</p>
          <Button onClick={() => fetchBriefing()} className="mt-4">Retry</Button>
        </div>
      </div>
    );
  }
  
  const health = briefing.health || {};
  const firstName = user?.name?.split(' ')[0] || user?.email?.split('@')[0] || 'there';
  
  return (
    <div className="min-h-screen bg-gradient-to-b from-slate-50 to-white" data-testid="ops-coordinator">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
        
        {/* ============ SECTION 1: HEADER & HEALTH INDEX ============ */}
        <header className="mb-8">
          <div className="flex flex-col lg:flex-row lg:items-start lg:justify-between gap-6">
            {/* Greeting & Summary */}
            <div className="flex-1">
              <div className="flex items-center gap-2 mb-1">
                <span className="text-xs uppercase tracking-wider text-slate-500 font-medium">
                  {new Date().toLocaleDateString('en-US', { weekday: 'long', month: 'long', day: 'numeric' })}
                </span>
                <button 
                  onClick={() => fetchBriefing(true)}
                  disabled={refreshing}
                  className="p-1 rounded hover:bg-slate-100 transition-colors"
                  title="Refresh briefing"
                >
                  <RefreshCw className={`w-3.5 h-3.5 text-slate-400 ${refreshing ? 'animate-spin' : ''}`} />
                </button>
              </div>
              <h1 className="text-2xl sm:text-3xl font-semibold text-slate-900 mb-2" data-testid="greeting">
                {getGreeting()}, {firstName}.
              </h1>
              
              {/* Executive Summary */}
              <div className="bg-white rounded-xl border border-slate-200 p-4 shadow-sm" data-testid="executive-summary">
                <div className="flex items-start gap-3">
                  <div className="w-8 h-8 rounded-lg bg-gradient-to-br from-blue-500 to-indigo-600 flex items-center justify-center shrink-0">
                    <Zap className="w-4 h-4 text-white" />
                  </div>
                  <div>
                    <p className="text-sm font-medium text-slate-500 mb-0.5">Today&apos;s Outlook</p>
                    <p className="text-slate-900 leading-relaxed">{briefing.executive_summary}</p>
                  </div>
                </div>
              </div>
            </div>
            
            {/* Health Ring */}
            <div className="flex items-center gap-6 bg-white rounded-xl border border-slate-200 p-4 shadow-sm lg:px-8">
              <HealthRing score={health.overall?.score || 0} />
              <div className="flex flex-col gap-1">
                <span className="text-xs uppercase tracking-wider text-slate-500 font-medium">Agency Health</span>
                <div className="flex items-center gap-2">
                  {getTrendIcon(health.overall?.trend)}
                  <span className="text-sm text-slate-600 capitalize">{health.overall?.trend || 'stable'}</span>
                </div>
                {health.overall?.factors?.length > 0 && (
                  <p className="text-xs text-slate-500 max-w-[180px] line-clamp-2 mt-1">
                    {health.overall.factors[0]}
                  </p>
                )}
              </div>
            </div>
          </div>
        </header>
        
        {/* ============ MAIN GRID ============ */}
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          
          {/* ============ LEFT COLUMN: PRIORITIES + HEALTH ============ */}
          <div className="lg:col-span-2 space-y-6">
            
            {/* Section 2: Today's Priorities */}
            <section data-testid="priorities-section">
              <div className="flex items-center justify-between mb-3">
                <h2 className="text-sm font-semibold uppercase tracking-wider text-slate-500 flex items-center gap-2">
                  <Target className="w-4 h-4" />
                  Today&apos;s Priorities
                </h2>
                <span className="text-xs text-slate-400">{briefing.priority_actions?.length || 0} actions</span>
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                {(briefing.priority_actions || []).slice(0, 5).map((action, i) => (
                  <PriorityCard key={action.id} action={action} index={i} onNavigate={handleNavigate} />
                ))}
                {(!briefing.priority_actions || briefing.priority_actions.length === 0) && (
                  <div className="col-span-2 text-center py-8 text-slate-500 bg-white rounded-xl border border-dashed border-slate-200">
                    <CheckCircle2 className="w-8 h-8 mx-auto mb-2 text-emerald-500" />
                    <p className="font-medium">All clear!</p>
                    <p className="text-sm">No urgent priorities right now.</p>
                  </div>
                )}
              </div>
            </section>
            
            {/* Section 3: Agency Health Grid */}
            <section data-testid="health-section">
              <h2 className="text-sm font-semibold uppercase tracking-wider text-slate-500 flex items-center gap-2 mb-3">
                <Activity className="w-4 h-4" />
                Agency Health
              </h2>
              <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
                <HealthMetric 
                  label="Compliance" 
                  score={health.compliance?.score || 0} 
                  trend={health.compliance?.trend}
                  icon={Shield}
                />
                <HealthMetric 
                  label="Scheduling" 
                  score={health.scheduling?.score || 0} 
                  trend={health.scheduling?.trend}
                  icon={Calendar}
                />
                <HealthMetric 
                  label="Documentation" 
                  score={health.documentation?.score || 0} 
                  trend={health.documentation?.trend}
                  icon={FileText}
                />
                <HealthMetric 
                  label="Client Care" 
                  score={health.client_care?.score || 0} 
                  trend={health.client_care?.trend}
                  icon={Heart}
                />
                <HealthMetric 
                  label="Payroll" 
                  score={health.payroll?.score || 0} 
                  trend={health.payroll?.trend}
                  icon={DollarSign}
                />
                <HealthMetric 
                  label="Billing" 
                  score={health.billing?.score || 0} 
                  trend={health.billing?.trend}
                  icon={Briefcase}
                />
                <HealthMetric 
                  label="Workforce" 
                  score={health.workforce?.score || 0} 
                  trend={health.workforce?.trend}
                  icon={Users}
                />
                <div 
                  className="rounded-lg border border-slate-200 bg-white p-3 flex flex-col justify-center items-center cursor-pointer hover:bg-slate-50 hover:border-slate-300 transition-colors"
                  onClick={() => handleNavigate('/compliance')}
                  title="View full health details"
                >
                  <BarChart3 className="w-5 h-5 text-slate-400 mb-1" />
                  <span className="text-xs text-slate-500">View Details</span>
                </div>
              </div>
            </section>
            
            {/* Section 7: Ask Leynix */}
            <section data-testid="ask-aligned-section">
              <h2 className="text-sm font-semibold uppercase tracking-wider text-slate-500 flex items-center gap-2 mb-3">
                <Sparkles className="w-4 h-4" />
                Ask Leynix
              </h2>
              <AskAligned onSubmit={handleAskAligned} isLoading={askLoading} />
              
              {/* AI Response */}
              {aiResponse && (
                <div className="mt-4 bg-white rounded-xl border border-slate-200 p-4" data-testid="ai-response">
                  <div className="flex items-start gap-3">
                    <div className="w-8 h-8 rounded-lg bg-gradient-to-br from-violet-500 to-purple-600 flex items-center justify-center shrink-0">
                      <Sparkles className="w-4 h-4 text-white" />
                    </div>
                    <div className="flex-1">
                      <p className="text-slate-900 whitespace-pre-wrap">{aiResponse.answer}</p>
                      {aiResponse.suggested_actions?.length > 0 && (
                        <div className="mt-3 flex flex-wrap gap-2">
                          {aiResponse.suggested_actions.slice(0, 3).map((action, i) => (
                            <button
                              key={i}
                              onClick={() => handleNavigate(action.url)}
                              className="text-xs px-3 py-1.5 rounded-full bg-blue-50 text-blue-700 hover:bg-blue-100 transition-colors"
                            >
                              {action.label}
                            </button>
                          ))}
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              )}
            </section>
          </div>
          
          {/* ============ RIGHT COLUMN: RISKS + OPPORTUNITIES + TIMELINE ============ */}
          <div className="space-y-6">
            
            {/* Section 4: Operational Risks */}
            <section data-testid="risks-section">
              <h2 className="text-sm font-semibold uppercase tracking-wider text-slate-500 flex items-center gap-2 mb-3">
                <AlertTriangle className="w-4 h-4" />
                Operational Risks
              </h2>
              <div className="space-y-2">
                {(briefing.risks || []).slice(0, 5).map((risk) => (
                  <RiskCard key={risk.id} risk={risk} onNavigate={handleNavigate} />
                ))}
                {(!briefing.risks || briefing.risks.length === 0) && (
                  <div className="text-center py-6 text-slate-500 bg-slate-50 rounded-lg border border-dashed border-slate-200">
                    <CheckCircle2 className="w-6 h-6 mx-auto mb-2 text-emerald-500" />
                    <p className="text-sm">No risks detected</p>
                  </div>
                )}
              </div>
            </section>
            
            {/* Section 5: Opportunities */}
            <section data-testid="opportunities-section">
              <h2 className="text-sm font-semibold uppercase tracking-wider text-slate-500 flex items-center gap-2 mb-3">
                <Sparkles className="w-4 h-4" />
                Opportunities
              </h2>
              <div className="space-y-2">
                {(briefing.opportunities || []).slice(0, 4).map((opp) => (
                  <OpportunityCard key={opp.id} opportunity={opp} onNavigate={handleNavigate} />
                ))}
                {(!briefing.opportunities || briefing.opportunities.length === 0) && (
                  <div className="text-center py-4 text-slate-500 bg-slate-50 rounded-lg border border-dashed border-slate-200">
                    <p className="text-sm">No opportunities right now</p>
                  </div>
                )}
              </div>
            </section>
            
            {/* Section 6: Executive Timeline */}
            <section data-testid="timeline-section">
              <h2 className="text-sm font-semibold uppercase tracking-wider text-slate-500 flex items-center gap-2 mb-3">
                <Clock className="w-4 h-4" />
                Today&apos;s Timeline
              </h2>
              <Card className="border-slate-200">
                <CardContent className="p-4">
                  {(briefing.timeline?.events || []).length > 0 ? (
                    <div className="space-y-1">
                      {briefing.timeline.events.slice(0, 8).map((event, i) => (
                        <TimelineEvent key={`${event.id}-${event.display_time}-${i}`} event={event} onNavigate={handleNavigate} />
                      ))}
                    </div>
                  ) : (
                    <div className="text-center py-6 text-slate-500">
                      <Calendar className="w-6 h-6 mx-auto mb-2 text-slate-400" />
                      <p className="text-sm">No timeline events</p>
                    </div>
                  )}
                </CardContent>
              </Card>
            </section>
            
            {/* Quick Stats Footer */}
            <div className="grid grid-cols-3 gap-2 text-center">
              <div className="bg-white rounded-lg border border-slate-200 p-3">
                <div className="text-lg font-bold text-slate-900">{briefing.visits_scheduled_today || 0}</div>
                <div className="text-[10px] uppercase tracking-wide text-slate-500">Visits Today</div>
              </div>
              <div className="bg-white rounded-lg border border-slate-200 p-3">
                <div className="text-lg font-bold text-blue-600">{briefing.visits_in_progress || 0}</div>
                <div className="text-[10px] uppercase tracking-wide text-slate-500">In Progress</div>
              </div>
              <div className="bg-white rounded-lg border border-slate-200 p-3">
                <div className={`text-lg font-bold ${briefing.visits_running_late > 0 ? 'text-red-600' : 'text-slate-900'}`}>
                  {briefing.visits_running_late || 0}
                </div>
                <div className="text-[10px] uppercase tracking-wide text-slate-500">Running Late</div>
              </div>
            </div>
            
          </div>
        </div>
        
        {/* ============ FOOTER: QUICK METRICS ============ */}
        <footer className="mt-8 pt-6 border-t border-slate-200">
          <div className="flex flex-wrap items-center justify-between gap-4 text-xs text-slate-500">
            <div className="flex items-center gap-6">
              <span>Payroll: <strong className={briefing.payroll_ready_pct >= 90 ? 'text-emerald-600' : 'text-amber-600'}>{Math.round(briefing.payroll_ready_pct || 0)}%</strong> ready</span>
              <span>Billing: <strong className={briefing.billing_ready_pct >= 90 ? 'text-emerald-600' : 'text-amber-600'}>{Math.round(briefing.billing_ready_pct || 0)}%</strong> ready</span>
              <span>Open Shifts: <strong className={briefing.open_shifts > 0 ? 'text-red-600' : 'text-slate-900'}>{briefing.open_shifts || 0}</strong></span>
              <span>Compliance Alerts: <strong className={briefing.critical_compliance > 0 ? 'text-red-600' : 'text-slate-900'}>{briefing.critical_compliance || 0}</strong></span>
            </div>
            <div className="text-slate-400">
              Last updated: {new Date(briefing.generated_at).toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit' })}
            </div>
          </div>
        </footer>
        
      </div>
    </div>
  );
}
